import { useSelector } from 'react-redux';
import { useCurrentUser } from '@/redux/services/portal/user.api.js';
import { 
	selectUsername, 
	selectUserEmail, 
	selectUserAvatar 
} from '@/redux/selectors/userProfile.selector.js';
import { extractUserInfoFromToken } from '@/utils/jwtUtils.js';
import { getIdTokenFromLocalStorage } from '@/utils/auth0Token';

/**
 * Custom hook that provides user profile information from Redux state.
 * Falls back to decoding JWT token or API user data if Redux state is not available.
 * 
 * Priority: Redux userProfile > JWT token > API user data
 * 
 * @returns {Object} User profile with username, email, and avatar
 */
export const useUserProfile = () => {
	// Get user profile from Redux (set from JWT token)
	const reduxUsername = useSelector(selectUsername);
	const reduxEmail = useSelector(selectUserEmail);
	const reduxAvatar = useSelector(selectUserAvatar);
	
	// Get API user data (from backend)
	const { data: apiUser } = useCurrentUser();
	
	// Fallback: decode from JWT token if Redux state is not available
	const surveyorToken = (!reduxUsername && !reduxEmail && !reduxAvatar) 
		? getIdTokenFromLocalStorage()
		: null;
	const jwtUserInfo = surveyorToken ? extractUserInfoFromToken(surveyorToken) : null;

	// Combine all sources with priority: Redux > JWT token > API
	return {
		username: reduxUsername || 
		          jwtUserInfo?.username || 
		          apiUser?.email?.split('@')[0] || 
		          apiUser?.name || 
		          null,
		email: reduxEmail || 
		       jwtUserInfo?.email || 
		       apiUser?.email || 
		       null,
		avatar: reduxAvatar || 
		        jwtUserInfo?.avatar || 
		        apiUser?.picture || 
		        apiUser?.avatar || 
		        null
	};
};

